import React, {useMemo} from 'react';

import {AdvancedSearchType} from 'src/openapi/admin/models/AdvancedSearchType';
import {DateRangeFilter} from 'src/openapi/admin/models/DateRangeFilter';
import {SavedSearchRequestSortField} from 'src/openapi/admin/models/SavedSearchRequest';
import {SortDirection} from 'src/openapi/admin/models/SortDirection';

import BoxLoading from '@pb/kit-component-box-loading';
import Group from '@pb/kit-component-group';
import {StickyElement} from '@pb/kit-component-sticky-element';
import Typography from '@pb/kit-component-typography';

import {SavedTable} from '../components/saved-table';
import {SearchPanel} from '../components/search-panel';
import {ContentTabs} from '../content-tabs';
import {useGetSavedContentGeneralAlert} from '../hooks/useGetSavedContentGeneralAlert';
import {useGetSavedSearches} from '../hooks/useGetSavedSearches';
import {useGetSearchesEntityIdsCounts} from '../hooks/useGetSearchesEntityIdsCounts';
import {useGetSharedSavedSearches} from '../hooks/useGetSharedSavedSearches';
import {mergeSavedSearchesData} from '../utils/merge-searches-data';

import {container, savedSearchesPanel, sharedSearchesPanel} from './../content.module.styl';


type Props = {
    userId: number,
};

export const Searches = ({userId}: Props) => {
    const {
        data: savedSearches,
        isLoading: savedSearchesLoading,
        currentPage: currentPageSavedSearches,
        pageSize: pageSizeSavedSearches,
        setCurrentPage: setCurrentPageSavedSearches,
        setPageSize: setPageSizeSavedSearches,
        searchQuery: savedSearchQuery,
        setSearchQuery: setSavedSearchQuery,
        // Sort and filter controls
        setSortField: setSavedSortField,
        setSortDirection: setSavedSortDirection,
        setTypeFilter: setSavedTypeFilter,
        setCreateDateFilter: setSavedCreateDateFilter,
        setLastRunDateFilter: setSavedLastRunDateFilter,
        setLastSharedDateFilter: setSavedLastSharedDateFilter,
        isEmptySearchCriteria: isEmptySavedSearchCriteria,
        totalCount: totalCountSavedSearches,
    } = useGetSavedSearches({
        userId,
    });

    const {
        data: sharedSavedSearches,
        isLoading: sharedSearchesLoading,
        currentPage: currentPageSharedSearches,
        pageSize: pageSizeSharedSearches,
        setCurrentPage: setCurrentPageSharedSearches,
        setPageSize: setPageSizeSharedSearches,
        searchQuery: sharedSearchQuery,
        setSearchQuery: setSharedSearchQuery,
        // Sort and filter controls
        setSortField: setSharedSortField,
        setSortDirection: setSharedSortDirection,
        setTypeFilter: setSharedTypeFilter,
        setCreateDateFilter: setSharedCreateDateFilter,
        setLastRunDateFilter: setSharedLastRunDateFilter,
        setLastSharedDateFilter: setSharedLastSharedDateFilter,
        isEmptySearchCriteria: isEmptySharedSearchCriteria,
        totalCount: totalCountSharedSearches,
    } = useGetSharedSavedSearches({
        userId,
    });

    const savedSearchIds = useMemo(() => {
        return Array.isArray(savedSearches?.searches) ? savedSearches.searches.map(search => search.id) : [];
    }, [savedSearches]);

    const sharedSearchIds = useMemo(() => {
        return Array.isArray(sharedSavedSearches?.searches)
            ? sharedSavedSearches.searches.map(search => search.id)
            : [];
    }, [sharedSavedSearches]);

    const {data: savedSearchesCountsData, isLoading: savedSearchesCountsLoading} = useGetSearchesEntityIdsCounts({
        userId,
        ids: savedSearchIds,
    });

    const {
        data: savedSearchesAlerts,
        isLoading: savedSearchesAlertsLoading,
    } = useGetSavedContentGeneralAlert({ids: savedSearchIds});

    const {data: sharedSearchesCountsData, isLoading: sharedSearchesCountsLoading} = useGetSearchesEntityIdsCounts({
        userId,
        ids: sharedSearchIds,
    });

    const {
        data: sharedSearchesAlerts,
        isLoading: sharedSearchesAlertsLoading,
    } = useGetSavedContentGeneralAlert({ids: sharedSearchIds});

    const changePageSizeSavedSearches = (_currentPage: number, newPageSize: number) => {
        setCurrentPageSavedSearches(1);
        setPageSizeSavedSearches(newPageSize);
    };

    const changePageSizeSharedSearches = (_currentPage: number, newPageSize: number) => {
        setCurrentPageSharedSearches(1);
        setPageSizeSharedSearches(newPageSize);
    };

    // Handlers for saved searches sort and filter
    const handleSavedSortChange = (field: SavedSearchRequestSortField, direction: SortDirection) => {
        setSavedSortField(field);
        setSavedSortDirection(direction);
    };

    const handleSavedTypeFilterChange = (filter: AdvancedSearchType[]) => {
        setSavedTypeFilter(filter);
        setCurrentPageSavedSearches(1);
    };

    const handleSavedDateFilterChange = (field: 'createDate' | 'lastRun' | 'lastShared', filter?: DateRangeFilter) => {
        setCurrentPageSavedSearches(1);
        switch (field) {
            case 'createDate':
                setSavedCreateDateFilter(filter);
                break;
            case 'lastRun':
                setSavedLastRunDateFilter(filter);
                break;
            case 'lastShared':
                setSavedLastSharedDateFilter(filter);
                break;
        }
    };

    // Handlers for shared searches sort and filter
    const handleSharedSortChange = (field: SavedSearchRequestSortField, direction: SortDirection) => {
        setSharedSortField(field);
        setSharedSortDirection(direction);
    };

    const handleSharedTypeFilterChange = (filter: AdvancedSearchType[]) => {
        setSharedTypeFilter(filter);
    };

    const handleSharedDateFilterChange = (field: 'createDate' | 'lastRun' | 'lastShared', filter?: DateRangeFilter) => {
        switch (field) {
            case 'createDate':
                setSharedCreateDateFilter(filter);
                break;
            case 'lastRun':
                setSharedLastRunDateFilter(filter);
                break;
            case 'lastShared':
                setSharedLastSharedDateFilter(filter);
                break;
        }
    };

    return (

        <Group direction="v" gap="none">
            <StickyElement id="sticky-element">
                <ContentTabs userId={userId} activeTab="searches"/>
                <Group direction="h" gap="xxl" className={savedSearchesPanel}>
                    <Typography fontWeight="semi-bold" variant="heading-level-3">
                        Saved Searches {totalCountSavedSearches ? `(${totalCountSavedSearches})` : ''}
                    </Typography>
                    <SearchPanel
                        searchQuery={savedSearchQuery}
                        setSavedSearchQuery={setSavedSearchQuery}
                        typeLabel="Search"
                        setCurrentPage={setCurrentPageSavedSearches}
                    />
                </Group>
            </StickyElement>

            <BoxLoading loading={savedSearchesLoading || sharedSearchesLoading}>
                <Group direction="v" gap="m" className={container}>
                    <SavedTable
                        data={mergeSavedSearchesData(
                            savedSearches.searches,
                            savedSearchesAlerts,
                            savedSearchesCountsData,
                        )}
                        savedSearchType="SEARCH"
                        savedTableType="OWNER"
                        currentPage={currentPageSavedSearches}
                        onPaginationChange={paginationData => {
                            setCurrentPageSavedSearches(paginationData.currentPage);
                            setPageSizeSavedSearches(paginationData.pageSize);
                        }}
                        totalCount={savedSearches?.totalCount || 0}
                        onPageSizeChange={changePageSizeSavedSearches}
                        userId={userId}
                        pageSize={pageSizeSavedSearches}
                        onSortChange={handleSavedSortChange}
                        onTypeFilterChange={handleSavedTypeFilterChange}
                        onDateFilterChange={handleSavedDateFilterChange}
                        isEmptySearchCriteria={isEmptySavedSearchCriteria}
                        isLoading={savedSearchesCountsLoading}
                        alertsLoading={savedSearchesAlertsLoading}
                    />

                    {(sharedSavedSearches.searches.length > 0 || !isEmptySharedSearchCriteria) && (
                        <Group className="pt-l" direction="v" gap="m">
                            <StickyElement>
                                <Group direction="h" gap="xxl" className={sharedSearchesPanel}>
                                    <Typography fontWeight="semi-bold" variant="heading-level-3">
                                        Shared Searches {totalCountSharedSearches ? `(${totalCountSharedSearches})` : ''}
                                    </Typography>
                                    <SearchPanel
                                        searchQuery={sharedSearchQuery}
                                        setSharedSearchQuery={setSharedSearchQuery}
                                        typeLabel="Search"
                                        setCurrentPage={setCurrentPageSharedSearches}
                                        sectionLabel="Shared"
                                    />
                                </Group>
                            </StickyElement>
                            <SavedTable
                                data={mergeSavedSearchesData(
                                    sharedSavedSearches.searches,
                                    sharedSearchesAlerts,
                                    sharedSearchesCountsData,
                                )}
                                savedSearchType="SEARCH"
                                savedTableType="SHARED"
                                currentPage={currentPageSharedSearches}
                                onPaginationChange={paginationData => {
                                    setCurrentPageSharedSearches(paginationData.currentPage);
                                    setPageSizeSharedSearches(paginationData.pageSize);
                                }}
                                totalCount={sharedSavedSearches?.totalCount || 0}
                                onPageSizeChange={changePageSizeSharedSearches}
                                userId={userId}
                                pageSize={pageSizeSharedSearches}
                                onSortChange={handleSharedSortChange}
                                onTypeFilterChange={handleSharedTypeFilterChange}
                                onDateFilterChange={handleSharedDateFilterChange}
                                isEmptySearchCriteria={isEmptySharedSearchCriteria}
                                isLoading={sharedSearchesCountsLoading}
                                alertsLoading={sharedSearchesAlertsLoading}
                            />
                        </Group>
                    )}
                </Group>
            </BoxLoading>
        </Group>
    );
};
